import { useContext, useEffect, useState, useCallback } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQueryClient, useIsFetching } from '@tanstack/react-query';

import {
  Flex,
  Text,
  Button,
  useColorModeValue,
  Tooltip,
  IconButton,
  Box,
  Collapse,
} from '@chakra-ui/react';

import {
  RepeatIcon,
  AddIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  SearchIcon,
} from '@chakra-ui/icons';

import Card from 'components/card/Card';

import UnassignedLeadList from 'views/leads/components/UnassignedLeadList';
import ActiveLeadsList from 'views/leads/components/ActiveLeadsList';
import LeadSearch from 'views/leads/components/LeadSearch';

import type { SortDirection, SortState } from 'components/table';

import { LEAD_ACTIVE_LIST_STATUSES } from 'views/leads/constants';
import { ROLE_ACCOUNT_ASSISTANT } from 'variables/constants';

import { AuthContext } from 'contexts/AuthContext';

const LEAD_FILTER_KEYS = ['search', 'source', 'workflow', 'status'] as const;
const LEAD_SORT_KEYS = ['sort_field', 'sort_direction'] as const;
const UNASSIGNED_PREFIX = 'unassigned_';
const ACTIVE_PREFIX = 'active_';

function buildQueryFromPrefixedParams(
  searchParams: URLSearchParams,
  prefix: string,
  keys: readonly string[],
): string {
  return keys
    .map((key) => {
      const v = searchParams.get(prefix + key);
      return v != null && v !== ''
        ? `${key}=${encodeURIComponent(v.trim())}`
        : null;
    })
    .filter(Boolean)
    .join('&');
}

function usePrefixedSort(prefix: string): SortState {
  const [searchParams, setSearchParams] = useSearchParams();

  const sortField = searchParams.get(`${prefix}sort_field`) ?? '';
  const sortDirection = (
    searchParams.get(`${prefix}sort_direction`) === 'desc' ? 'desc' : 'asc'
  ) as SortDirection;

  const onSort = useCallback(
    (field: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          const isSameField = params.get(`${prefix}sort_field`) === field;
          const nextDirection =
            isSameField && params.get(`${prefix}sort_direction`) !== 'desc'
              ? 'desc'
              : 'asc';
          params.set(`${prefix}sort_field`, field);
          params.set(`${prefix}sort_direction`, nextDirection);
          return params;
        },
        { replace: true },
      );
    },
    [prefix, setSearchParams],
  );

  return { sortField, sortDirection, onSort };
}

function parseQueryToRecord(query: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!query) return out;
  query.split('&').forEach((part) => {
    const eq = part.indexOf('=');
    if (eq > 0) {
      const k = decodeURIComponent(part.slice(0, eq)).trim();
      const v = decodeURIComponent(part.slice(eq + 1)).trim();
      if (LEAD_FILTER_KEYS.includes(k as any)) out[k] = v;
    }
  });
  return out;
}

const LeadsView = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const isFetchingACtiveLeads = useIsFetching({
    queryKey: ['active-lead-list'],
  });
  const isFetchingUnassignedLeads = useIsFetching({
    queryKey: ['unassigned-lead-list'],
  });

  const [activeLeadsExpanded, setActiveLeadsExpanded] = useState(true);
  const [unassignedLeadsExpanded, setUnassignedLeadsExpanded] = useState(true);
  const [showActiveSearchFilters, setShowActiveSearchFilters] = useState(false);
  const [showUnassignedSearchFilters, setShowUnassignedSearchFilters] =
    useState(false);
  const [unassignedLeadCount, setUnassignedLeadCount] = useState(0);
  const [activeLeadCount, setActiveLeadCount] = useState(0);

  const [searchParams, setSearchParams] = useSearchParams();
  const unassignedSearchQuery = buildQueryFromPrefixedParams(
    searchParams,
    UNASSIGNED_PREFIX,
    LEAD_FILTER_KEYS,
  );
  const activeSearchQuery = buildQueryFromPrefixedParams(
    searchParams,
    ACTIVE_PREFIX,
    LEAD_FILTER_KEYS,
  );
  const unassignedSortQuery = buildQueryFromPrefixedParams(
    searchParams,
    UNASSIGNED_PREFIX,
    LEAD_SORT_KEYS,
  );
  const activeSortQuery = buildQueryFromPrefixedParams(
    searchParams,
    ACTIVE_PREFIX,
    LEAD_SORT_KEYS,
  );

  const unassignedSort = usePrefixedSort(UNASSIGNED_PREFIX);
  const activeSort = usePrefixedSort(ACTIVE_PREFIX);

  const handleUnassignedSearch = useCallback(
    (query: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('unassigned_query');
          const record = parseQueryToRecord(query);
          LEAD_FILTER_KEYS.forEach((key) => {
            const paramKey = UNASSIGNED_PREFIX + key;
            if (record[key]) params.set(paramKey, record[key]);
            else params.delete(paramKey);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleActiveSearch = useCallback(
    (query: string) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.delete('active_query');
          const record = parseQueryToRecord(query);
          LEAD_FILTER_KEYS.forEach((key) => {
            const paramKey = ACTIVE_PREFIX + key;
            if (record[key]) params.set(paramKey, record[key]);
            else params.delete(paramKey);
          });
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const refreshUnassignedLeads = () => {
    queryClient.invalidateQueries({ queryKey: ['unassigned-lead-list'] });
  };

  const refreshActiveLeads = () => {
    queryClient.invalidateQueries({ queryKey: ['active-lead-list'] });
  };

  const handleNewLead = () => {
    navigate('/admin/leads/create');
  };

  const handleSetUnassignedLeadCount = useCallback((count) => {
    setUnassignedLeadCount(count);
  }, []);

  const handleSetActiveLeadCount = useCallback((count) => {
    setActiveLeadCount(count);
  }, []);

  return (
    <>
      {user.role !== ROLE_ACCOUNT_ASSISTANT && (
        <Card
          flexDirection="column"
          w="100%"
          px="0px"
          overflowX={{ sm: 'scroll', lg: 'hidden' }}
          mb="20px"
        >
          <Flex px="20px" align="center" justify="space-between">
            <Text
              color={textColor}
              fontSize="22px"
              fontWeight="700"
              lineHeight="100%"
            >
              {`Unassigned Leads ${
                unassignedLeadCount > 0 ? `(${unassignedLeadCount})` : ''
              }`}
            </Text>
            <Flex align="center" gap={2}>
              <Tooltip
                label={
                  showUnassignedSearchFilters ? 'Hide search' : 'Show search'
                }
                hasArrow
              >
                <IconButton
                  aria-label={
                    showUnassignedSearchFilters ? 'Hide search' : 'Show search'
                  }
                  icon={<SearchIcon />}
                  onClick={() =>
                    setShowUnassignedSearchFilters(!showUnassignedSearchFilters)
                  }
                  size="md"
                  variant={showUnassignedSearchFilters ? 'solid' : 'ghost'}
                  colorScheme={showUnassignedSearchFilters ? 'yellow' : 'gray'}
                />
              </Tooltip>

              <Tooltip label="Refresh leads" hasArrow>
                <IconButton
                  aria-label="Refresh leads"
                  icon={<RepeatIcon />}
                  onClick={refreshUnassignedLeads}
                  variant="ghost"
                  isLoading={isFetchingUnassignedLeads > 0}
                />
              </Tooltip>

              {![ROLE_ACCOUNT_ASSISTANT].includes(user.role) && (
                <Button
                  leftIcon={<AddIcon />}
                  onClick={handleNewLead}
                  variant="brand"
                  fontSize="sm"
                >
                  New Lead
                </Button>
              )}

              <Tooltip
                label={unassignedLeadsExpanded ? 'Collapse' : 'Expand'}
                hasArrow
              >
                <IconButton
                  aria-label={
                    unassignedLeadsExpanded
                      ? 'Collapse section'
                      : 'Expand section'
                  }
                  icon={
                    unassignedLeadsExpanded ? (
                      <ChevronUpIcon />
                    ) : (
                      <ChevronDownIcon />
                    )
                  }
                  onClick={() =>
                    setUnassignedLeadsExpanded(!unassignedLeadsExpanded)
                  }
                  variant="ghost"
                />
              </Tooltip>
            </Flex>
          </Flex>

          <Collapse in={showUnassignedSearchFilters} animateOpacity>
            <Box mt="20px">
              <LeadSearch
                onSearch={handleUnassignedSearch}
                initialSearch={searchParams.get('unassigned_search') ?? ''}
                initialSource={searchParams.get('unassigned_source') ?? ''}
                initialWorkflow={searchParams.get('unassigned_workflow') ?? ''}
                initialStatus={searchParams.get('unassigned_status') ?? ''}
              />
            </Box>
          </Collapse>

          <Collapse in={unassignedLeadsExpanded} animateOpacity>
            <UnassignedLeadList
              searchQuery={unassignedSearchQuery}
              sortQuery={unassignedSortQuery}
              sort={unassignedSort}
              setLeadCount={handleSetUnassignedLeadCount}
            />
          </Collapse>
        </Card>
      )}

      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="20px" align="center" justify="space-between">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            {`Active Leads ${
              activeLeadCount > 0 ? `(${activeLeadCount})` : ''
            }`}
          </Text>
          <Flex align="center" gap={2}>
            <Tooltip
              label={showActiveSearchFilters ? 'Hide search' : 'Show search'}
              hasArrow
            >
              <IconButton
                aria-label={
                  showActiveSearchFilters ? 'Hide search' : 'Show search'
                }
                icon={<SearchIcon />}
                onClick={() =>
                  setShowActiveSearchFilters(!showActiveSearchFilters)
                }
                size="md"
                variant={showActiveSearchFilters ? 'solid' : 'ghost'}
                colorScheme={showActiveSearchFilters ? 'yellow' : 'gray'}
              />
            </Tooltip>

            <Tooltip label="Refresh leads" hasArrow>
              <IconButton
                aria-label="Refresh leads"
                icon={<RepeatIcon />}
                onClick={refreshActiveLeads}
                variant="ghost"
                isLoading={isFetchingACtiveLeads > 0}
              />
            </Tooltip>

            {![ROLE_ACCOUNT_ASSISTANT].includes(user.role) && (
              <Button
                leftIcon={<AddIcon />}
                onClick={handleNewLead}
                variant="brand"
                fontSize="sm"
              >
                New Lead
              </Button>
            )}

            <Tooltip
              label={activeLeadsExpanded ? 'Collapse' : 'Expand'}
              hasArrow
            >
              <IconButton
                aria-label={
                  activeLeadsExpanded ? 'Collapse section' : 'Expand section'
                }
                icon={
                  activeLeadsExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />
                }
                onClick={() => setActiveLeadsExpanded(!activeLeadsExpanded)}
                variant="ghost"
              />
            </Tooltip>
          </Flex>
        </Flex>

        <Collapse in={showActiveSearchFilters} animateOpacity>
          <Box mt="20px">
            <LeadSearch
              onSearch={handleActiveSearch}
              leadStatues={LEAD_ACTIVE_LIST_STATUSES}
              initialSearch={searchParams.get('active_search') ?? ''}
              initialSource={searchParams.get('active_source') ?? ''}
              initialWorkflow={searchParams.get('active_workflow') ?? ''}
              initialStatus={searchParams.get('active_status') ?? ''}
            />
          </Box>
        </Collapse>

        <Collapse in={activeLeadsExpanded} animateOpacity>
          <ActiveLeadsList
            searchQuery={activeSearchQuery}
            sortQuery={activeSortQuery}
            sort={activeSort}
            setLeadCount={handleSetActiveLeadCount}
          />
        </Collapse>
      </Card>
    </>
  );
};

export default LeadsView;
